import { Box, Stack } from "@dropbox/dig-foundations";
import classNames from "classnames";
import * as React from "react";
import { v4 as uuid4 } from "uuid";
import styles from "./DraftShowcase.module.css";

type DraftLine = {
  id: string;
  width: number;
};

const createDraftLine = (index: number) => {
  // Generate deterministic but varied widths using mathematical operations
  // This creates widths in the range 60-95% that look random but are consistent
  const variation = Math.sin(index * 1.7) * Math.cos(index * 0.3) * 17.5;
  const width = 77.5 + variation; // Center at 77.5% with ±17.5% variation

  return {
    id: `draftLine-${uuid4()}`,
    width: Math.round(width * 10) / 10, // Round to 1 decimal place for cleaner values
  };
};

export interface DraftShowcaseProps {
  /**
   * The number of rows to display in the mock document
   */
  rows?: number;

  /**
   * Whether to animate the skeleton elements
   */
  animate?: boolean;
}

/**
 * DraftShowcase displays a skeleton of the draft document page.
 */
export const DraftShowcase: React.FC<DraftShowcaseProps> = ({
  rows = 4,
  animate = true,
}) => {
  // Track the next index to use for new lines (continues incrementing during animations)
  const nextLineIndexRef = React.useRef(rows);

  const [draftLines, setDraftLines] = React.useState<DraftLine[]>(
    Array.from({ length: rows }, (_, index) => createDraftLine(index)),
  );

  const [animationCycleState, setAnimationCycleState] = React.useState<
    "filling_bottom" | "scrolling_up"
  >("filling_bottom");

  const handleAnimationEnd = (event: React.AnimationEvent) => {
    if (!animate) return;

    switch (animationCycleState) {
      case "filling_bottom":
        if (event.animationName.includes("extend")) {
          setAnimationCycleState("scrolling_up");
        }
        break;
      case "scrolling_up":
        if (event.animationName.includes("fade-out")) {
          setDraftLines((prev) => {
            const newLine = createDraftLine(nextLineIndexRef.current++);
            return [...prev.slice(1), newLine];
          });
          setAnimationCycleState("filling_bottom");
        }
        break;
    }
  };

  return (
    <Box
      width="100%"
      borderRadius="Large"
      borderColor="Border Subtle"
      borderWidth="1"
      paddingX="20"
      paddingTop="24"
      paddingBottom="100"
      boxShadow="Raised"
    >
      <Stack width="100%" height="100%" gap="20">
        <Box className={styles.titleSkeleton} data-testid="title-skeleton" />
        <Stack width="100%" gap="20">
          {draftLines.map((line, index) => (
            <div
              onAnimationEnd={handleAnimationEnd}
              className={classNames(styles.lineSkeletonContainer, {
                [styles.shiftUpAnimate]:
                  animate &&
                  animationCycleState === "scrolling_up" &&
                  index !== 0,
                [styles.shiftUpFadeOutAnimate]:
                  animate &&
                  animationCycleState === "scrolling_up" &&
                  index === 0,
              })}
              key={line.id}
              style={{ width: `${line.width}%` }}
              data-testid="line-skeleton-container"
            >
              <div
                onAnimationEnd={handleAnimationEnd}
                className={classNames(styles.lineSkeleton, {
                  [styles.extendAnimate]:
                    animate &&
                    index === draftLines.length - 1 &&
                    animationCycleState === "filling_bottom",
                })}
              />
            </div>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
